/**
 * Cambia per quanto resta pubblicato un documento.
 *
 * Come «Sposta in…», e' ammesso anche su un documento gia' pubblicato: non tocca
 * il file, il suo nome ne' alcun indirizzo — cambia solo il giorno in cui il
 * documento uscira' dalla pubblicazione corrente. Lo si dice nella modale,
 * perche' chi la apre su un documento online si chiede proprio questo.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { ErroreApi, impostaDurataDocumento } from "../api/client";
import type { Documento } from "../api/types";
import { leggiDurata, sceltaDelDocumento } from "../lib/scadenze";
import type { SceltaDurata } from "../lib/scadenze";
import { CampoDurata } from "./CampoDurata";
import { Modale } from "./Modale";
import { Avviso, Bottone } from "./primitive";

export function ModaleDurata({
  documento,
  contesto,
  durataObbligo,
  regoleNote = true,
  onChiudi,
  onCompletato,
}: {
  documento: Pick<Documento, "id" | "titolo" | "durata_anni" | "fino_al" | "fine_pubblicazione">;
  contesto?: ReactNode;
  durataObbligo?: number | null;
  /** Falso se le regole degli obblighi non sono (ancora) disponibili. */
  regoleNote?: boolean;
  onChiudi: () => void;
  onCompletato: (aggiornato: Documento) => void;
}) {
  const iniziale = sceltaDelDocumento(documento);
  const [scelta, setScelta] = useState<SceltaDurata>(iniziale.scelta);
  const [finoAl, setFinoAl] = useState(iniziale.finoAl);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const letta = leggiDurata(scelta, finoAl);
  const invariata = scelta === iniziale.scelta && finoAl.trim() === iniziale.finoAl;

  const salva = async () => {
    if (letta.errore || invariata) return;
    setErrore(null);
    setInCorso(true);
    try {
      onCompletato(
        await impostaDurataDocumento(documento.id, {
          durata_anni: letta.durata_anni,
          fino_al: letta.fino_al,
        }),
      );
    } catch (causa) {
      setErrore(causa instanceof ErroreApi ? causa.messaggioEsteso : "Durata non salvata.");
    } finally {
      setInCorso(false);
    }
  };

  const chiudi = () => {
    if (!inCorso) onChiudi();
  };

  return (
    <Modale
      titolo={`Durata di «${documento.titolo}»`}
      aperta
      onChiudi={chiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={chiudi} disabled={inCorso}>
            Annulla
          </Bottone>
          <Bottone
            variante="primario"
            inCorso={inCorso}
            disabled={Boolean(letta.errore) || invariata}
            onClick={() => void salva()}
          >
            Salva la durata
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {contesto ? <div>{contesto}</div> : null}
        {errore ? (
          <Avviso tono="critico" titolo="Durata non salvata">
            {errore}
          </Avviso>
        ) : null}
        {documento.fine_pubblicazione ? (
          <p>
            Oggi resta pubblicato fino al <strong>{documento.fine_pubblicazione}</strong>.
          </p>
        ) : null}
        <CampoDurata
          scelta={scelta}
          finoAl={finoAl}
          durataObbligo={durataObbligo}
          regoleNote={regoleNote}
          onCambia={(nuova, data) => {
            setScelta(nuova);
            setFinoAl(data);
          }}
        />
        <p className="campo__aiuto">
          Cambiare la durata non cambia il file, il suo nome ne' l'indirizzo con cui e' pubblicato:
          cambia solo il giorno in cui uscira' dalla pubblicazione corrente.
        </p>
      </div>
    </Modale>
  );
}
